import { t } from '@app/i18n';
import type { ApiBody, ApiData } from '@app/api/typed';
// A postura é a MESMA da T21 e do Runtime, e por isso o vocabulário vem de um
// lugar só. `SitePostureModel` nasceu em `TenantSettings/` com a P5, e o próprio
// arquivo dizia que se mudaria para cá «quando aquele drawer nascer» — nasceu, e
// ele veio junto (P1, com «pode» do lead). A T21 continua importando de cá: o
// dono do vocabulário é o Site, não o tenant. Duplicá-lo seria o oposto: dois
// lugares para a mesma regra, discordando no dia de um quarto nível.
import {
  lowers,
  POSTURES_FALLBACK,
  postureHelp,
  postureLabel,
  type SitePosture,
} from './SitePostureModel';

/**
 * Modelo da tela «Sites» (P1, grupo Controle).
 *
 * O que esta tela é, em uma frase: o cadastro dos CLUSTERS de um tenant. Um site
 * é onde um workload roda — o executor, o APIM, o PDP — e é a âncora do
 * endereçamento (P7): o recurso carrega o site, a identidade carrega o site, e o
 * SPIFFE ID sai do template DO SITE.
 */

export type SiteList = ApiData<'/api/v1/sites'>;
export type SiteItem = SiteList['items'][number];
export type SiteCreate = ApiBody<'/api/v1/sites', 'post'>;
export type SiteUpdate = ApiBody<'/api/v1/sites/{id}', 'put'>;
export type SiteConnectionTest = ApiData<'/api/v1/sites/{id}/test', 'post'>;
export type SiteStatus = SiteItem['status'];

export type { SitePosture };
export { lowers, POSTURES_FALLBACK, postureHelp, postureLabel };

/** `^[a-z0-9-]+$` — o `pattern` de `SiteCreate.slug` no contrato. */
export const SLUG_PATTERN = /^[a-z0-9-]+$/;

/**
 * O charset do trust domain — espelho de `domain.TrustDomainValid` (minúsculas,
 * dígitos, ponto e hífen). O servidor recusa com `campos[].code =
 * trustDomainCharset`, o MESMO código do tenant; a tela recusa antes, com a mesma
 * regra, para o operador não descobrir por 422 o que dá para dizer enquanto digita.
 */
export const TRUST_DOMAIN_PATTERN = /^[a-z0-9.-]+$/;

export const STATUSES: readonly SiteStatus[] = ['ativo', 'inativo'];

/**
 * Rótulo do estado. Função, e não tabela de módulo: um `Record` de texto é
 * avaliado no import e congela o idioma da primeira pintura — o achado do
 * `qa-i18n` que valeu para `SEV_LABEL` e `TITLE_DEFAULT`.
 */
export function statusLabel(status: SiteStatus): string {
  return t(`sites.status.${status}`);
}

export interface SiteFormValues {
  /** Imutável depois de criado — vai para dentro do SPIFFE ID. */
  slug: string;
  name: string;
  executorEndpoint: string;
  /**
   * O trust domain DESTE site (S-b) — o primeiro segmento de todo SPIFFE ID que ele
   * emite. Sites podem repartir o mesmo (SPIRE replicado) ou ter o seu (federação).
   */
  trustDomain: string;
  posture: SitePosture;
  /** Vazio = «use o padrão». Ver `siteDefaults`, que é onde isso importa. */
  spiffeTemplate: string;
  status: SiteStatus;
  /** Header CF-Access-Client-Id da borda DESTE site (S-a). */
  cfClientId: string;
  /**
   * Header CF-Access-Client-Secret. **Nasce sempre vazio**, mesmo num site que já
   * tem segredo: o valor nunca volta do servidor (o DTO diz só `cf_secret_set`),
   * e vazio significa «mantém o gravado». Ver `forUpdate`.
   */
  cfSecret: string;
}

/**
 * Valores iniciais do drawer.
 *
 * # A ARMADILHA DO `spiffe_template` (handoff do `p1-multisite`)
 *
 * O `spiffe_template` do DTO é o **efetivo** — nunca vazio: a coluna nula lê como
 * o template de hoje. Quem diz se ALGUÉM ESCOLHEU é `spiffe_template_explicit`.
 *
 * O campo mostra **vazio, com placeholder**, quando `explicit` é falso. Mostrar o
 * efetivo preenchido faria o operador salvar sem querer o padrão como escolha
 * explícita — e a partir daquele save o site deixaria de acompanhar mudanças do
 * padrão, sem ninguém ter pedido isso. É o estado do site `default`, então o erro
 * aconteceria na primeira edição do primeiro site de todo cliente.
 */
export function siteDefaults(site?: SiteItem): SiteFormValues {
  return {
    slug: site?.slug ?? '',
    name: site?.name ?? '',
    executorEndpoint: site?.executor_endpoint ?? '',
    trustDomain: site?.trust_domain ?? '',
    posture: site?.posture ?? 'artifact_binding',
    spiffeTemplate: site?.spiffe_template_explicit ? site.spiffe_template : '',
    status: site?.status ?? 'ativo',
    cfClientId: site?.cf_client_id ?? '',
    // SEMPRE vazio, inclusive num site que tem segredo: ele não volta do servidor,
    // e o campo vazio é o que diz «não mexi». Preenchê-lo com um valor de fachada
    // (`'••••'`) faria o operador salvar essa fachada como segredo novo.
    cfSecret: '',
  };
}

/**
 * O texto que o campo de template mostra em cinza quando ninguém escolheu.
 *
 * Site NOVO: o `template_default` da lista — o com segmento `site/`, que é o que
 * garante IDs únicos entre clusters. Site EXISTENTE sem escolha: o efetivo dele,
 * que para o `default` é o de hoje, sem o segmento. São textos diferentes de
 * propósito: o placeholder tem de dizer o que vai valer se o campo ficar vazio,
 * e para esses dois sites isso não é a mesma coisa.
 */
export function templatePlaceholder(site: SiteItem | undefined, listDefault: string): string {
  if (site && !site.spiffe_template_explicit) return site.spiffe_template;
  return listDefault;
}

export function forCreate(values: SiteFormValues): SiteCreate {
  return {
    slug: values.slug.trim(),
    name: values.name.trim(),
    executor_endpoint: values.executorEndpoint.trim(),
    trust_domain: values.trustDomain.trim(),
    posture: values.posture,
    // Enviado SEMPRE, inclusive vazio: no contrato o vazio é «use o padrão», que
    // é uma escolha e não uma omissão. Omitir o campo diria a mesma coisa hoje, e
    // deixaria de dizer no dia em que o PATCH parcial existir.
    spiffe_template: values.spiffeTemplate.trim(),
    status: values.status,
    cf_client_id: values.cfClientId.trim(),
    cf_client_secret: values.cfSecret,
  };
}

/** O `slug` **não** entra: renomeá-lo moveria o SPIFFE ID de identidades já emitidas. */
export function forUpdate(values: SiteFormValues): SiteUpdate {
  return {
    name: values.name.trim(),
    executor_endpoint: values.executorEndpoint.trim(),
    // O TRUST DOMAIN VAI SEMPRE, e o dia em que ele deixar de ir o Salvar o APAGA.
    //
    // O PUT é substituição: campo ausente é campo vazio, e a coluna nasceu no S-b
    // (a) — entre aquele marco e este, todo Salvar do drawer zerava o trust domain
    // do site em silêncio, e nada ficava vermelho porque nenhuma tela ainda o
    // mandava. É o mesmo defeito que o cf_client_id teve no S-a, pela mesma razão:
    // uma coluna entrou no payload e o formulário não entrou junto.
    trust_domain: values.trustDomain.trim(),
    posture: values.posture,
    spiffe_template: values.spiffeTemplate.trim(),
    status: values.status,
    // O `cf_client_id` vai SEMPRE, inclusive vazio: no contrato ele é o campo cujo
    // vazio APAGA e cuja ausência mantém. Este formulário é o dono dele agora, então
    // omiti-lo faria a tela perder a única forma de apagar uma credencial.
    cf_client_id: values.cfClientId.trim(),
    // O SEGREDO só vai quando foi digitado — e vazio já significa «mantém» do outro
    // lado. Sem `trim()`: espaço numa senha pode ser da senha; quem apara é o
    // servidor, que sabe que cabeçalho HTTP não carrega quebra de linha.
    cf_client_secret: values.cfSecret,
  };
}

/**
 * Busca da lista, do lado do cliente — e isto é escolha, não atalho.
 *
 * A coleção `/api/v1/sites` não pagina nem filtra no contrato, porque um tenant
 * tem punhado de clusters, não milhares. Mandar a busca ao servidor exigiria
 * parâmetro novo no contrato para uma tela que já tem a lista inteira em mãos.
 */
export function matches(site: SiteItem, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return (
    site.name.toLowerCase().includes(q) ||
    site.slug.toLowerCase().includes(q) ||
    site.executor_endpoint.toLowerCase().includes(q)
  );
}

/**
 * Quanto o site carrega — a pré-condição do «Excluir».
 *
 * Zero identidades E zero recursos é o único estado em que o botão aparece; o
 * banco recusa com RESTRICT de qualquer forma, e o `default` recusa com
 * `409 site_padrao`. A tela conta ANTES para não oferecer um botão que só pode
 * falhar (FR-010 é sobre papel, isto é sobre estado — a mesma ideia).
 */
export function isEmpty(site: SiteItem): boolean {
  return site.identities === 0 && site.resources === 0;
}

export function canDelete(site: SiteItem): boolean {
  return !site.is_default && isEmpty(site);
}
